import { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { useTranslate } from '@client/i18n/hooks';
import { BoxError } from '@client/myAccount/common/components/BoxError';
import { MyAccount } from '@client/myAccount/common/components/MyAccount';
import { MY_ACCOUNT_MENU_ITEM } from '@client/myAccount/common/utils/myAccountMenu';
import { USERS_LIST_DEFAULT_PAGE_SIZE } from '@client/myAccount/costCenters/consts';
import {
  useCostCenterData,
  useCostCenterUsersData,
  useFetchCostCenter,
  useFetchCostCenterUsers,
  useRemoveCostCenterUser,
} from '@client/myAccount/costCenters/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useRouterPush, useUrlCreator } from '@client/routes/hooks';
import { openLink } from '@client/routes/utils/url';
import emitter, { EVENT } from 'b2b-apps/core/common/utils/eventEmitter';
import { UsersSkeleton } from '../UsersSkeleton';
import { ListPage } from './ListPage';

export const ListPageContainer = (): ReactElement => {
  const t = useTranslate();
  const { costCenterId, page: paramsPage } = useParams<{ costCenterId: string, page: string }>();
  const push = useRouterPush();
  const getCostCenterDetails = useFetchCostCenter();
  const costCenterDetailsRecord = useCostCenterData(costCenterId);
  const fetchCostCenterUsers = useFetchCostCenterUsers();
  const costCenterUsersRecord = useCostCenterUsersData();
  const urlCreator = useUrlCreator();
  const removeUser = useRemoveCostCenterUser();

  const handleRemove = (userId: string): void => {
    removeUser({ costCenterId, userId });
  };

  const myAccountUrl = urlCreator(ROUTES.myAccount);
  const backToCostCentersList = urlCreator(ROUTES.myAccountCostCenters, {
    page: 1,
  });
  const costCenterUrl = urlCreator(ROUTES.myAccountCostCenter, {
    costCenterId: decodeURI(costCenterId),
  });

  const handleAddUser = () => {
    const canCreateUser = costCenterUsersRecord.data?.costCenter.canCreateUser;

    if (canCreateUser) {
      const url = urlCreator(ROUTES.myAccountCostCentersUsersCreate, { costCenterId });
      openLink(url, push);
    }
  };

  const handleEditUser = (userId: string): void => {
    const canCreateUser = costCenterUsersRecord.data?.costCenter.canCreateUser;

    if (canCreateUser) {
      const url = userId
        ? urlCreator(ROUTES.myAccountCostCentersUsersUpdate, { costCenterId, userId })
        : urlCreator(ROUTES.myAccountCostCentersUsersCreate, { costCenterId });
      openLink(url, push);
    }
  };

  const page = parseInt(paramsPage, 10) || 1;
  const handlePageChange = (page: number) => {
    openLink(
      urlCreator(ROUTES.myAccountCostCenterUsers, {
        costCenterId,
        page,
      }),
      push,
    );
  };

  useBreadcrumbs(
    [
      {
        title: t('My account'),
        url: myAccountUrl,
      },
      {
        title: t('Cost Centers'),
        url: backToCostCentersList,
      },
      {
        title: costCenterDetailsRecord?.data?.label || '...',
        url: costCenterUrl,
      },
      {
        title: t('Manage Users'),
      },
    ],
  );

  useEffect(() => {
    emitter.on(EVENT.removeCostCenterUserFulfilled, () => {
      fetchCostCenterUsers({ costCenterId, page: 1, pageSize: USERS_LIST_DEFAULT_PAGE_SIZE });
    });

    return () => {
      emitter.off(EVENT.removeCostCenterUserFulfilled);
    };
  }, []);

  useEffect(() => {
    getCostCenterDetails(costCenterId);
  }, [costCenterId]);

  useEffect(() => {
    fetchCostCenterUsers({ costCenterId, page, pageSize: USERS_LIST_DEFAULT_PAGE_SIZE });
  }, [paramsPage]);

  const loaderComponent = (
    <div className='cds-CardBox'>
      <div className='cds-CardBox-Title'>
        <h2 className='cds-CardBox-TitleHeading cds-CardBox-TitleHeading--Large CostCentersListPage-Heading'>
          {t('Users')}
        </h2>
      </div>
      <UsersSkeleton />
    </div>
  );

  return (
    <MyAccount active={MY_ACCOUNT_MENU_ITEM.costCenters}>
      {(costCenterUsersRecord.isLoading || costCenterDetailsRecord.isLoading) && loaderComponent}
      {((costCenterUsersRecord.isLoaded && costCenterDetailsRecord.isLoaded)
        && (!costCenterUsersRecord.hasFailed && !costCenterDetailsRecord.hasFailed))
        && costCenterUsersRecord.data
        && (
          <ListPage
            backButtonUrl={costCenterUrl}
            data={costCenterUsersRecord.data}
            page={page}
            onPageChange={handlePageChange}
            id={costCenterDetailsRecord?.data?.label || ''}
            description={costCenterDetailsRecord?.data?.description || null}
            onAdd={handleAddUser}
            onEdit={handleEditUser}
            onRemove={handleRemove}
          />
        )}
      {(costCenterUsersRecord.hasFailed || costCenterDetailsRecord.hasFailed) && (
        <BoxError title={t('Manage Users')} remoteRecord={costCenterUsersRecord} />
      )}
    </MyAccount>
  );
};
